import type { ExportColumn, ExportRow } from './table-export.service';
import type { TableFilterButtonPropsOptions } from '@openng/optimus-ui/table';

export const SMALL_TABLE_FILTER_BUTTON_PROPS: TableFilterButtonPropsOptions = {
  filter: { size: 'small', text: true, rounded: true },
  inline: undefined,
  popover: undefined,
};

const identifierPattern =
  /(^|_)(id|ids|identifier|identifiers|barcode|accession|locus|loci|report|reports|link|links|name|names|chromosome|sequence)(_|$)/;
const sampleIdentifierPattern = /(^|_)samples?($|_(id|name|barcode|identifier))/;
const geneIdentifierPattern = /(^|_)gene($|_(name|symbol|id)(_|$))/;

export function normalizeNumericTableRows<T extends ExportRow>(columns: ExportColumn[], rows: T[]): T[] {
  const numericFields = new Set(
    columns.filter((column) => isNumericTableColumn(column, rows)).map((column) => column.data),
  );

  return rows.map((row) => {
    const normalizedRow: ExportRow = { ...row };
    for (const field of numericFields) {
      const value = normalizedRow[field];
      if (typeof value === 'string' && value.trim()) {
        normalizedRow[field] = Number(value.replace(/,/g, '').trim());
      }
    }
    return normalizedRow as T;
  });
}

export function isNumericTableColumn(column: ExportColumn, rows: ExportRow[]): boolean {
  if (isIdentifierColumn(column)) {
    return false;
  }

  const values = rows.map((row) => row[column.data]).filter((value) => !isMissingValue(value));
  return values.length > 0 && values.every((value) => parseNumericValue(value) !== null);
}

function isIdentifierColumn(column: ExportColumn): boolean {
  const key = column.data.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  const title = column.title.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  return [key, title].some(
    (field) =>
      identifierPattern.test(field) || sampleIdentifierPattern.test(field) || geneIdentifierPattern.test(field),
  );
}

function isMissingValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && (!value.trim() || /^(?:-|–|—|n\/?a|null)$/i.test(value.trim())))
  );
}

function parseNumericValue(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string' || isMissingValue(value)) {
    return null;
  }

  const text = value.trim();
  if (!/^[+-]?(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(text)) {
    return null;
  }
  if (/^[+-]?0\d+$/.test(text)) {
    return null;
  }

  const parsed = Number(text.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}
